import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AnalysisProvider } from "./context/AnalysisContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Pricing from "./pages/Pricing";
import GuidesIndex from "./pages/guides/GuidesIndex";
import GuideArticle from "./pages/guides/GuideArticle";
import AuthorPage from "./pages/authors/AuthorPage";
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Upload = lazy(() => import("./pages/Upload"));
const Inventory = lazy(() => import("./pages/Inventory"));
const ReceiptInbox = lazy(() => import("./pages/ReceiptInbox"));
const Settings = lazy(() => import("./pages/Settings"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const GoogleVerification = lazy(() => import("./pages/GoogleVerification"));
const ClaimsIndex = lazy(() => import("./pages/claims/ClaimsIndex"));
const NewClaim = lazy(() => import("./pages/claims/NewClaim"));
const ClaimWorkspace = lazy(() => import("./pages/claims/ClaimWorkspace"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const GoogleAuth = lazy(() => import("./pages/GoogleAuth"));
const AdminCredits = lazy(() => import("./pages/AdminCredits"));
import MobileTabBar from "./components/MobileTabBar";
import InstallPrompt from "./components/InstallPrompt";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return (
    <>
      {children}
      <MobileTabBar />
      <InstallPrompt />
    </>
  );
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="spinner" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const location = useLocation();
  // CRITICAL: process Google OAuth return (session_id in URL fragment) BEFORE any route/guard runs.
  // Read the hash from useLocation() so React re-renders after it is cleared.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-slate-50"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" /></div>}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        } 
      />
      <Route 
        path="/verify-email" 
        element={<VerifyEmail />} 
      />
      <Route 
        path="/privacy" 
        element={<PrivacyPolicy />} 
      />
      <Route 
        path="/terms" 
        element={<TermsOfService />} 
      />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/google-verification" element={<GoogleVerification />} />
      <Route path="/guides" element={<GuidesIndex />} />
      <Route path="/guides/:slug" element={<GuideArticle />} />
      <Route path="/auth/google" element={<GoogleAuth />} />
      <Route path="/authors/:slug" element={<AuthorPage />} />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upload" 
        element={
          <ProtectedRoute>
            <Upload />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inventory" 
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/receipt-inbox" 
        element={
          <ProtectedRoute>
            <ReceiptInbox />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/credits"
        element={
          <ProtectedRoute>
            <AdminCredits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/claims"
        element={
          <ProtectedRoute>
            <ClaimsIndex />
          </ProtectedRoute>
        }
      />
      <Route
        path="/claims/new"
        element={
          <ProtectedRoute>
            <NewClaim />
          </ProtectedRoute>
        }
      />
      <Route
        path="/claims/:id"
        element={
          <ProtectedRoute>
            <ClaimWorkspace />
          </ProtectedRoute>
        }
      />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="app-container">
        <BrowserRouter>
          <AuthProvider>
            <AnalysisProvider>
              <AppRoutes />
              <Toaster position="top-right" />
            </AnalysisProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
